/**
 * wayai status — show current auth/config status and repo scope
 *
 * Usage:
 *   wayai status          # human-readable output
 *   wayai status --json   # structured output for agents/scripts
 *
 * The --json shape is the canonical state snapshot consumed by the WayAI skill
 * during agentic onboarding. Fields are nullable so the agent can branch
 * cleanly from any starting state (no config / no org / no workspace / etc.).
 */

import { readConfig } from '../lib/config.js';
import { getAccessToken } from '../lib/auth.js';
import {
  readRepoScopeBlocker,
  resolveRepoConfig,
  type RepoScopeBlocker,
  type ResolvedRepoConfig,
} from '../lib/repo-config.js';
import { ROOT_CONFIG_FILE, WORKSPACE_MANIFEST_LABEL } from '../lib/workspace-manifest.js';
import { detectWorkspace, findGitRoot, scanWorkspaceHubs } from '../lib/workspace.js';
import { emptyScope, readScope, type WorktreeScope } from '../lib/worktree-scope.js';
import { ApiClient, ApiError } from '../lib/api-client.js';
import { CLI_CACHE_FILE, SKILL_CACHE_FILE, readCachedLatest } from '../lib/version-cache.js';
import {
  findInstalledSkills,
  lowestInstalledVersion,
} from '../lib/skill-version.js';
import { isNewerVersion } from '../lib/utils.js';
import { nudgeReportActions } from '../lib/report-inbox.js';

export function parseArgs(args: string[]): { json: boolean } {
  return { json: args.includes('--json') };
}

interface StatusSnapshot {
  logged_in: boolean;
  token_valid: boolean | null;
  api_url: string | null;
  repo_scope: { organization_id: string; organization_name: string | null } | null;
  cli: { version: string; latest: string | null };
  skill: {
    installed: boolean;
    version: string | null;
    latest: string | null;
    paths: string[];
  };
  auth: { logged_in: boolean; user_email: string | null };
  orgs: Array<{ id: string; name: string }>;
  active_org: { id: string; name: string } | null;
  workspace: { scoped: boolean; path: string | null; hub_count: number };
  /**
   * Why an UNSCOPED repo is unscoped, when the reason is something the user must
   * act on rather than "nothing declared yet" (null in every ordinary state).
   *
   * Without it `scoped: false` reads identically for "brand-new repo" and "two
   * files name different organizations" — and the skill's onboarding table
   * branches on exactly that flag, so it would answer the second with
   * `wayai init`, which rewrites one of the two files and cannot resolve a
   * disagreement between them.
   */
  repo_scope_blocker: RepoScopeBlocker | null;
  /**
   * The whole worktree scope: every hub and every base this checkout is scoped
   * to (`lib/worktree-scope.ts`). The authoritative shape — the two fields below
   * are the single-valued views that predate it.
   */
  worktree_scope: { hubs: string[]; bases: string[] };
  // The FIRST hub of the scope, or null. Kept because the public skill's
  // progress-report reads `worktree_binding.hub_id` to pick which hub a report
  // covers, and a multi-hub scope has no better single answer than its first.
  worktree_binding: { hub_id: string | null };
  // Deprecated alias of `worktree_binding`, kept so existing `status --json`
  // consumers don't break across the lock→binding rename. Always mirrors it.
  worktree_lock: { hub_id: string | null };
}

function buildSkillState(): StatusSnapshot['skill'] {
  const projectRoot = findGitRoot() ?? process.cwd();
  const installs = findInstalledSkills(projectRoot);
  if (installs.length === 0) {
    return { installed: false, version: null, latest: null, paths: [] };
  }
  const version = lowestInstalledVersion(installs);
  const cached = readCachedLatest(SKILL_CACHE_FILE);
  // Field semantics: "newer available, or null". The cache can lag the
  // installed version (24h TTL; `npx skills add ...` doesn't invalidate it),
  // so suppress the field unless it actually advertises an upgrade.
  const latest = cached && version && isNewerVersion(cached, version) ? cached : null;
  return {
    installed: true,
    version,
    latest,
    paths: installs.map((i) => i.path),
  };
}

/**
 * `path` is the file that actually supplied the org — the workspace manifest,
 * or the deprecated root binding when this repo has not been migrated yet.
 * Taken from the resolution rather than re-derived, so it can never name a file
 * that did not answer.
 */
function buildWorkspaceState(resolved: ResolvedRepoConfig | null): StatusSnapshot['workspace'] {
  if (!resolved) return { scoped: false, path: null, hub_count: 0 };

  const ws = detectWorkspace();
  const hubCount = ws ? scanWorkspaceHubs(ws.workspaceDir).length : 0;
  return { scoped: true, path: resolved.path, hub_count: hubCount };
}

export async function statusCommand(
  args: string[],
  pkg: { version: string }
): Promise<void> {
  const { json } = parseArgs(args);
  const config = readConfig();
  const resolvedRepo = resolveRepoConfig();
  const repoConfig = resolvedRepo?.config ?? null;
  const workspace = buildWorkspaceState(resolvedRepo);
  // Only asked when nothing answered — a resolved repo has no blocker by
  // definition, and this re-reads the same two files to find out why.
  const repoScopeBlocker = resolvedRepo ? null : readRepoScopeBlocker();
  // Same "newer available, or null" gate as buildSkillState.
  const cachedCliLatest = readCachedLatest(CLI_CACHE_FILE);
  const cliLatest = cachedCliLatest && isNewerVersion(cachedCliLatest, pkg.version)
    ? cachedCliLatest
    : null;
  const skill = buildSkillState();
  // Status is a diagnostic command — never crash on a scope-file read error.
  // Everywhere else an unreadable scope propagates (fail-secure); here it reads
  // as "no scope", because a diagnostic that dies on the state it reports is
  // worse than one that under-reports it.
  let scope: WorktreeScope;
  try {
    scope = readScope();
  } catch {
    scope = emptyScope();
  }
  const boundHubId = scope.hubs[0] ?? null;

  // Cold-start path: no config at all.
  if (!config) {
    const snapshot: StatusSnapshot = {
      logged_in: false,
      token_valid: null,
      api_url: null,
      repo_scope: null,
      cli: { version: pkg.version, latest: cliLatest },
      skill,
      auth: { logged_in: false, user_email: null },
      orgs: [],
      active_org: null,
      workspace,
      repo_scope_blocker: repoScopeBlocker,
      worktree_scope: scope,
      worktree_binding: { hub_id: boundHubId },
      worktree_lock: { hub_id: boundHubId },
    };

    if (json) {
      console.log(JSON.stringify(snapshot, null, 2));
    } else {
      console.log('Not logged in. Run `wayai login`.');
    }
    return;
  }

  // Logged-in path: validate token against the backend, then enrich with data.
  // token_valid reflects backend acceptance (not just storage load) so users
  // can't be fooled into thinking auth works when API calls 401.
  let accessToken: string | null = null;
  let tokenLoaded = false;
  try {
    accessToken = await getAccessToken(config);
    tokenLoaded = true;
  } catch {
    tokenLoaded = false;
  }

  let userEmail: string | null = null;
  let orgs: Array<{ id: string; name: string }> = [];
  // An empty-string accessToken bypasses the API-call branch below, so flag
  // it as definitively-invalid up front rather than leaving it as the
  // "validity unknown" sentinel.
  let tokenValid: boolean | null = tokenLoaded && accessToken ? null : false;

  if (tokenLoaded && accessToken) {
    const client = new ApiClient({ apiUrl: config.api_url, accessToken });
    const [meResult, orgsResult] = await Promise.allSettled([
      client.me(),
      client.organizations(),
    ]);
    if (meResult.status === 'fulfilled') {
      userEmail = meResult.value.email || null;
      tokenValid = true;
    } else if (meResult.reason instanceof ApiError && meResult.reason.isExpected) {
      // Any 4xx is the backend deciding "this token isn't acceptable" — 401
      // (invalid/expired/stale), 403 (scope revoked, deletion-pending),
      // 404 (user gone). Treat all as definitive rejection.
      tokenValid = false;
    }
    // Other failures (network unreachable, 5xx) leave tokenValid as null —
    // "couldn't determine" is more truthful than "valid" or "invalid".
    if (orgsResult.status === 'fulfilled') {
      orgs = orgsResult.value.organizations.map((o) => ({ id: o.id, name: o.name }));
    }
  }

  const activeOrg = repoConfig
    ? {
        id: repoConfig.organization_id,
        name:
          orgs.find((o) => o.id === repoConfig.organization_id)?.name
          ?? repoConfig.organization_name
          ?? repoConfig.organization_id,
      }
    : null;

  const snapshot: StatusSnapshot = {
    logged_in: true,
    token_valid: tokenValid,
    api_url: config.api_url,
    repo_scope: repoConfig
      ? {
          organization_id: repoConfig.organization_id,
          organization_name: repoConfig.organization_name ?? null,
        }
      : null,
    cli: { version: pkg.version, latest: cliLatest },
    skill,
    auth: { logged_in: true, user_email: userEmail },
    orgs,
    active_org: activeOrg,
    workspace,
    repo_scope_blocker: repoScopeBlocker,
    worktree_scope: scope,
    worktree_binding: { hub_id: boundHubId },
    worktree_lock: { hub_id: boundHubId },
  };

  if (json) {
    console.log(JSON.stringify(snapshot, null, 2));
    return;
  }

  // Human-readable output.
  const tokenStatus =
    tokenValid === true
      ? 'token valid'
      : tokenValid === false
        ? 'token rejected — run `wayai login` to re-authenticate'
        : 'token validity unknown — backend unreachable';
  console.log(`Logged in${userEmail ? ` as ${userEmail}` : ''} — ${tokenStatus}`);

  if (config.api_url !== 'https://api.wayai.pro') {
    console.log(`API: ${config.api_url}`);
  }

  if (cliLatest) {
    console.log(`CLI: ${pkg.version} (latest: ${cliLatest} — run \`wayai update\`)`);
  }

  if (skill.installed && skill.latest) {
    console.log(
      `Skill: ${skill.version} (latest: ${skill.latest} — run \`npx skills add wayai-pro/wayai-skill -y\`)`
    );
  }

  if (orgs.length > 0) {
    console.log(`Organizations: ${orgs.length}`);
  }

  if (repoConfig) {
    const orgLine = repoConfig.organization_name
      ? `${repoConfig.organization_name} (${repoConfig.organization_id})`
      : repoConfig.organization_id;
    console.log(`Repository scope:`);
    console.log(`  Organization: ${orgLine}`);
    console.log(`  Hubs: ${workspace.hub_count}`);
  } else if (repoScopeBlocker?.kind === 'conflict') {
    // NOT "run `wayai init`": init rewrites the manifest, and the disagreement
    // is between the two files, so it would survive.
    console.log('Repository scope: UNRESOLVED — two files name different organizations:');
    console.log(`  ${WORKSPACE_MANIFEST_LABEL} -> ${repoScopeBlocker.manifest_organization_id}`);
    console.log(`  ${ROOT_CONFIG_FILE} -> ${repoScopeBlocker.root_organization_id}`);
    console.log(`  Delete or correct ${ROOT_CONFIG_FILE}; until then every command refuses.`);
  } else if (repoScopeBlocker?.kind === 'invalid') {
    console.log(`Repository scope: UNRESOLVED — ${repoScopeBlocker.path} is unusable (${repoScopeBlocker.detail}).`);
  } else {
    console.log(
      `No organization binding found in ${WORKSPACE_MANIFEST_LABEL} — run \`wayai init\` to scope this repo to an organization.`,
    );
  }

  // Both axes, so a base-scoped worktree is visible here too — it was not, and
  // the base guard's refusal was the first place a user learned of it.
  if (scope.hubs.length > 0 || scope.bases.length > 0) {
    console.log('Worktree scope: (run `wayai unbind` to clear)');
    if (scope.hubs.length > 0) console.log(`  Hubs:  ${scope.hubs.join(', ')}`);
    if (scope.bases.length > 0) console.log(`  Bases: ${scope.bases.join(', ')}`);
  }

  // Best-effort, non-JSON path only (the --json branch returned above): remind the reporter of
  // any reports awaiting their accept/contest. Self-guards on TTY; only runs with a valid token.
  if (tokenValid === true && accessToken) {
    await nudgeReportActions(config.api_url, accessToken);
  }
}
